/* eslint-disable jsx-a11y/no-static-element-interactions */
import "./QuickNews.css";

import { UpdateGADataForArticle } from "@components/Modules/LoadMore/UpdateGADataForArticle";
import {
  renderFeedCard,
  renderVideoCard,
} from "@components/Modules/QuickNews/QnCardHelper";
import QnCardNav from "@components/Modules/QuickNews/QnCardNav";
import QnFooter from "@components/Modules/QuickNews/QnFooter";
import QnSideNav from "@components/Modules/QuickNews/QnSideNav";
import QnTopNav from "@components/Modules/QuickNews/QnTopNav";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { useBHVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useBHVirtualPVAnalyticsCall";
import { Route } from "@route-types/QuickNews/+types/QuickNews";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
} from "@sphtech/web2-core/ssr";
import QnOnboard from "@src/app/components/Modules/QuickNews/QnOnboard";
import { getMetaData } from "@src/app/helper/getMetaData";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { FeedItem, QNCardType, VideoItem } from "@typings/QuickNews";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import BHQuickNewsBasePage from "../BHQuickNewsBasePage";
import ErrorPage from "../ErrorPage";
import NotFound from "../NotFound";
import { getCardStyle } from "./helper/QnCardStyle";
import {
  useInfiniteContent,
  useInfiniteVideos,
} from "./helper/QuickNewsHelper";
import fetchQNData, { QuickNewsDataPayload } from "./QuickNews.server";
import QuickNewsMetaData from "./QuickNewsMetaData";

export function meta({ loaderData }: Route.MetaArgs) {
  const dataPayload =
    typeof loaderData === "object" &&
    loaderData !== null &&
    "payload" in loaderData
      ? loaderData.payload
      : undefined;
  const metaTags =
    typeof dataPayload === "object" &&
    dataPayload !== null &&
    "metaTags" in dataPayload
      ? (dataPayload as QuickNewsDataPayload).metaTags
      : undefined;
  if (!metaTags) {
    return [{ title: "Quick News" }];
  }
  const metaRes = getMetaData(metaTags);
  return [...metaRes];
}

export default function QuickNews(): ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    QuickNewsDataPayload,
    string
  > = useLoaderData();

  // Call all hooks before any conditional returns
  const bhVirtualPV = useBHVirtualPVAnalyticsCall();
  const BHCustomEvent = useBHCustomEventAnalyticsCall();

  const isSuccess = dataLoaderResponse.type === ResponseType.SUCCESS;

  // Extract data safely for hook initialization (use defaults for error cases)
  const Stories =
    isSuccess && dataLoaderResponse.payload.recentArticles
      ? dataLoaderResponse.payload.recentArticles
      : [];
  const bcVideos =
    isSuccess && dataLoaderResponse.payload.bcVideos
      ? dataLoaderResponse.payload.bcVideos
      : [];

  const initialActiveTab = isSuccess
    ? dataLoaderResponse.payload.initialActiveTab
    : "MyFeed";
  const initialId = isSuccess
    ? dataLoaderResponse.payload.initialId
    : undefined;

  const {
    content: articles,
    hasMore,
    loadMore,
    isInitialized,
    loadMoreOlder,
  } = useInfiniteContent({
    Stories,
    initialActiveTab,
    initialId,
  });

  const { videos, hasMoreVideos, loadMoreVideos } = useInfiniteVideos({
    bcVideos,
    initialId,
    initialActiveTab,
  });

  const [activeTab, setActiveTab] = useState<QNCardType>(initialActiveTab);
  const containerRef = useRef<HTMLDivElement>(null);
  const [articleIndex, setArticleIndex] = useState(0);
  const [videoIndex, setVideoIndex] = useState(0);
  const [isMute, setIsMute] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isScrolling = useRef(false);
  const [onBoarded, setOnBoarded] = useState(!!initialId);

  const cards = activeTab === "MyFeed" ? articles : videos;
  const currentIndex = activeTab === "MyFeed" ? articleIndex : videoIndex;
  const setCurrentIndex =
    activeTab === "MyFeed" ? setArticleIndex : setVideoIndex;

  const getEventLabel = useCallback(
    (card: FeedItem | VideoItem) =>
      "title" in card ? card.title : "name" in card ? card.name : "",
    [],
  );

  // Responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Tab switching resets indices
  const setPushState = useCallback(
    (tab: QNCardType, activeIndex?: number) => {
      const index = activeIndex ?? currentIndex;
      if (
        tab === "MyFeed" &&
        articles[index] &&
        !("adType" in articles[index] || "endCard" in articles[index])
      ) {
        const item = articles[index];
        window.history.pushState(
          "article",
          item.title,
          `/quick-news/${item.id}`,
        );
        setOnBoarded(true);
      } else if (
        tab === "Videos" &&
        videos[index] &&
        !("endCard" in videos[index])
      ) {
        const item = videos[index];
        window.history.pushState(
          "video",
          item.name,
          `/quick-news/video/${item.id}`,
        );
        setOnBoarded(true);
      }
    },
    [articles, videos, currentIndex],
  );

  const setMetaTags = useCallback(
    (activeTab: QNCardType, index?: number) => {
      const currentIndex =
        index ?? (activeTab === "MyFeed" ? articleIndex : videoIndex);
      const card =
        activeTab === "MyFeed" ? articles[currentIndex] : videos[currentIndex];
      const { metaTags } = QuickNewsMetaData({
        card,
        activeTab,
        onBoarded: true,
        pagination: currentIndex,
      });

      document.title = metaTags.title || "";
      const metaDescription = document.querySelector<HTMLMetaElement>(
        'meta[name="description"]',
      );
      if (metaDescription) {
        metaDescription.content = metaTags.description || "";
      }
      const metaCanonical = document.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]',
      );
      if (metaCanonical) {
        metaCanonical.href = `${window.location.origin}/${metaTags.slug || ""}`;
      }
    },
    [articles, videos, articleIndex, videoIndex],
  );

  const switchTab = useCallback(
    (tab: QNCardType, eventType?: string) => {
      setActiveTab(tab);
      setPushState(tab);

      setMetaTags(tab);

      const eventActionMap: Record<string, string> = {
        keyboard: "keyboard press",
        mouse: "mouse drag",
        touch: "touch swipe",
      };
      const eventActionType = eventActionMap[eventType || ""] || "click";
      const platform = eventType === "touch" ? "mobile" : "desktop";
      const nextIndex =
        activeTab === "MyFeed" ? articleIndex + 1 : videoIndex + 1;
      const eventAction = `${platform}::${eventActionType}::${nextIndex}`;
      const eventLabel = tab;
      BHCustomEvent("quick-news", eventAction, eventLabel, undefined, "");
    },
    [
      setPushState,
      articleIndex,
      videoIndex,
      BHCustomEvent,
      setMetaTags,
      activeTab,
    ],
  );

  const trackCardNavigation = useCallback(
    (direction: "previous" | "next", eventType?: string) => {
      const eventActionMap: Record<string, string> = {
        keyboard: "keyboard press",
        scroll: "mouse scroll",
        mouse: "mouse drag",
        touch: "touch swipe",
      };
      const eventActionType = eventActionMap[eventType || ""] || "click";
      const platform = eventType === "touch" ? "mobile" : "desktop";
      const nextIndex =
        activeTab === "MyFeed" ? articleIndex + 1 : videoIndex + 1;
      const currentTypeIndex =
        direction === "next" ? nextIndex : currentIndex - 1;
      const currentCard =
        activeTab === "MyFeed"
          ? articles[currentTypeIndex]
          : videos[currentTypeIndex];
      const eventAction = `${platform}::${eventActionType} ${direction}::${direction == "next" ? currentTypeIndex : currentTypeIndex + 1}`;

      BHCustomEvent(
        "quick-news",
        eventAction,
        getEventLabel(currentCard),
        undefined,
        "",
      );

      if (activeTab === "MyFeed" && !("adType" in articles[currentTypeIndex])) {
        bhVirtualPV(
          articles[currentTypeIndex].title,
          undefined,
          undefined,
          undefined,
          currentTypeIndex + 1,
        );
        if (
          !("endCard" in articles[currentTypeIndex]) &&
          !("quizzes" in articles[currentTypeIndex])
        ) {
          UpdateGADataForArticle(articles[currentTypeIndex], true);
        }
      }
    },
    [
      activeTab,
      articleIndex,
      videoIndex,
      currentIndex,
      articles,
      videos,
      getEventLabel,
      BHCustomEvent,
      bhVirtualPV,
    ],
  );

  // Infinite content loading
  useEffect(() => {
    const fetchMoreFeed = async () => {
      if (articles.length - articleIndex === 3 && hasMore && isInitialized)
        await loadMore();
    };
    fetchMoreFeed().catch(console.error);
  }, [articleIndex, articles.length, loadMore, hasMore, isInitialized]);

  const getOlderArticles = useCallback(() => {
    loadMoreOlder();
    void loadMore();
  }, [loadMoreOlder, loadMore]);

  useEffect(() => {
    const fetchMoreVideos = async () => {
      if (videos.length - videoIndex === 3 && hasMoreVideos && isInitialized)
        await loadMoreVideos();
    };
    fetchMoreVideos().catch(console.error);
  }, [videoIndex, videos.length, loadMoreVideos, hasMoreVideos, isInitialized]);

  // Prevent pull-to-refresh and iOS bounce
  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";
    const preventDefault = (e: TouchEvent) => {
      if (e.touches.length > 1) return; // Allow multi-touch gestures
      e.preventDefault();
    };
    document.addEventListener("touchmove", preventDefault, { passive: false });
    return () => {
      document.body.style.overscrollBehavior = "";
      document.documentElement.style.overscrollBehavior = "";
      document.removeEventListener("touchmove", preventDefault);
    };
  }, [isMobile]);

  // Card Navigation
  const prevCard = useCallback(
    (eventType?: string) => {
      if (
        (activeTab === "MyFeed" && articleIndex === 0) ||
        (activeTab === "Videos" && videoIndex === 0)
      ) {
        window.location.href = "/quick-news";
        setOnBoarded(false);
        return;
      }

      setCurrentIndex((prev) => prev - 1);
      setPushState(activeTab, currentIndex - 1);
      setMetaTags(activeTab, currentIndex - 1);
      trackCardNavigation("previous", eventType);
    },
    [
      activeTab,
      articleIndex,
      videoIndex,
      setCurrentIndex,
      trackCardNavigation,
      setPushState,
      currentIndex,
      setMetaTags,
    ],
  );

  const nextCard = useCallback(
    (eventType?: string) => {
      if (
        (activeTab === "MyFeed" && articleIndex >= articles.length - 1) ||
        (activeTab === "Videos" && videoIndex >= videos.length - 1)
      )
        return;
      setCurrentIndex((prev) => prev + 1);
      setPushState(activeTab, currentIndex + 1);
      setMetaTags(activeTab, currentIndex + 1);
      trackCardNavigation("next", eventType);
    },
    [
      activeTab,
      articleIndex,
      articles.length,
      videoIndex,
      videos.length,
      setCurrentIndex,
      trackCardNavigation,
      setPushState,
      currentIndex,
      setMetaTags,
    ],
  );

  // Drag/Touch state
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startX, setStartX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [offsetX, setOffsetX] = useState(0);

  // Handle keyboard navigation
  const handleStart = (clientY: number, clientX: number) => {
    setIsDragging(true);
    setStartY(clientY);
    setStartX(clientX);
  };
  const handleMove = (clientY: number, clientX: number) => {
    if (!isDragging) return;
    setOffsetY(clientY - startY);
    setOffsetX(clientX - startX);
  };

  const handleEnd = useCallback(
    (eventHandler: string) => {
      setIsDragging(false);
      const horizontalThreshold = 50,
        verticalThreshold = 30;
      if (
        Math.abs(offsetX) > Math.abs(offsetY) &&
        Math.abs(offsetX) > horizontalThreshold
      ) {
        if (offsetX > 0) {
          switchTab("MyFeed", eventHandler);
        } else {
          switchTab("Videos", eventHandler);
        }
      } else if (Math.abs(offsetY) > Math.abs(offsetX)) {
        if (offsetY > verticalThreshold) {
          if (currentIndex === 0) {
            window.location.href = "/quick-news";
          } else {
            prevCard(eventHandler);
          }
        } else if (
          offsetY < -verticalThreshold &&
          currentIndex < cards.length - 1
        ) {
          nextCard(eventHandler);
        }
      }
      setOffsetY(0);
      setOffsetX(0);
    },
    [
      offsetX,
      offsetY,
      currentIndex,
      cards.length,
      prevCard,
      nextCard,
      switchTab,
    ],
  );

  // Use effect to handle the Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (currentIndex < cards.length - 1 && onBoarded) {
          nextCard("keyboard");
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (currentIndex > 0 && onBoarded) {
          prevCard("keyboard");
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (onBoarded) {
          switchTab("MyFeed", "keyboard");
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (onBoarded) {
          switchTab("Videos", "keyboard");
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIndex, cards.length, prevCard, nextCard, switchTab, onBoarded]);

  // Use effect to handle the mouse wheel navigation for cards
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (isScrolling.current) return;
      isScrolling.current = true;
      if (e.deltaY > 0 && currentIndex < cards.length - 1) nextCard("scroll");
      else if (e.deltaY < 0 && currentIndex > 0) prevCard("scroll");
      setTimeout(() => (isScrolling.current = false), 200);
    };
    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [currentIndex, cards.length, prevCard, nextCard]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) handleEnd("mouse");
    };
    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, handleEnd]);

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  return (
    <BHQuickNewsBasePage>
      <QnOnboard
        isMobile={isMobile}
        activeTab={activeTab}
        initialId={initialId}
        setPushState={setPushState}
        article={
          activeTab === "MyFeed"
            ? (articles[0] as ProcessedArticleData)
            : undefined
        }
      />
      <QnTopNav activeTab={activeTab} setActiveTab={switchTab} />
      <div className="side-nav">
        <QnSideNav activeTab={activeTab} setActiveTab={switchTab} />
      </div>

      {/* Quick News Content */}
      <div
        ref={containerRef}
        className="qn-card-container relative w-full h-[calc(100svh-90px)] px-2.5 overflow-hidden cursor-grab active:cursor-grabbing select-none mt-4"
        style={{ touchAction: "none", overscrollBehavior: "none" }}
        onMouseDown={(e) => {
          handleStart(e.clientY, e.clientX);
        }}
        onMouseMove={(e) => {
          handleMove(e.clientY, e.clientX);
        }}
        onMouseUp={() => {
          handleEnd("mouse");
        }}
        onMouseLeave={() => {
          handleEnd("mouse");
        }}
        onTouchStart={(e) => {
          handleStart(e.touches[0].clientY, e.touches[0].clientX);
        }}
        onTouchMove={(e) => {
          handleMove(e.touches[0].clientY, e.touches[0].clientX);
        }}
        onTouchEnd={() => {
          handleEnd("touch");
        }}
      >
        <div className="qn-card-wrapper relative w-full md:w-[450px] h-full mx-auto">
          {/* eslint-disable-next-line react-hooks/refs */}
          {articles.map((card, index) => (
            <div
              key={`qn-article-${card.id}`}
              className="absolute inset-0 flex items-center justify-center h-full md:h-[80svh]"
              style={{
                ...getCardStyle(
                  index,
                  "MyFeed",
                  activeTab === "MyFeed",
                  articleIndex,
                  videoIndex,
                  isDragging,
                  offsetY,
                  isMobile,
                  containerRef,
                ),
                transition:
                  isDragging && activeTab === "MyFeed"
                    ? "none"
                    : "all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.175)",
                pointerEvents:
                  index === articleIndex && activeTab === "MyFeed"
                    ? "auto"
                    : "none",
              }}
            >
              <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
                {renderFeedCard({
                  card,
                  isMobile,
                  nextCard,
                  getOlderArticles,
                  typeIndex: articleIndex,
                })}
              </div>
            </div>
          ))}

          {/* eslint-disable-next-line react-hooks/refs */}
          {videos.map((card, index) => (
            <div
              key={`qn-video-${card.id}`}
              className="absolute inset-0 flex items-center justify-center h-full md:h-[80svh]"
              style={{
                ...getCardStyle(
                  index,
                  "Videos",
                  activeTab === "Videos",
                  articleIndex,
                  videoIndex,
                  isDragging,
                  offsetY,
                  isMobile,
                  containerRef,
                ),
                transition:
                  isDragging && activeTab === "Videos"
                    ? "none"
                    : "all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.175)",
                pointerEvents:
                  index === videoIndex && activeTab === "Videos"
                    ? "auto"
                    : "none",
              }}
            >
              <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden">
                {renderVideoCard({
                  card,
                  index,
                  isMute,
                  setIsMute,
                  activeTab,
                  videoIndex,
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <QnCardNav
        activeTab={activeTab}
        canPrevFeed={articleIndex > 0}
        canPrevVideo={videoIndex > 0}
        canNextFeed={articleIndex < articles.length - 1}
        canNextVideo={videoIndex < videos.length - 1}
        onPrev={prevCard}
        onNext={nextCard}
      />
      <QnFooter />
    </BHQuickNewsBasePage>
  );
}

export async function loader({ params }: Route.LoaderArgs) {
  const id = params.id || undefined;
  const vid = params.vid || undefined;
  const data = await fetchQNData({ id, vid });
  // const { gaData } =QuickNewsMetaData();

  return {
    ...data,
    // gtm: getGTMData(gaData),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as TRouteWithoutRedirect<
    QuickNewsDataPayload,
    string
  > | null;

  if (!result) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult = result;

  // Check for valid payload field
  if (!("payload" in typedResult)) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult;
}
